import type { PlayerConfig, SourceType } from '@movingimage-evp/player-library';
import type { GetVideoProblemCode, Video, VideoManager } from 'src/generated/graphql-manager';

import { useCallback, useEffect, useState } from 'react';
import { useGetVideoQuery } from 'src/generated/graphql-manager';

export type VideoData = {
  title: string;
  playerData: Exclude<PlayerConfig, 'metadata'> & {
    metadata: { title?: string; durationInMs?: number; videoId?: string };
  };
  structuredData: {
    uploadDate: string;
  };
};

export function useGetVideo(videoManagerId?: VideoManager['id'], videoId?: Video['id']) {
  const [playerConfig, setPlayerConfig] = useState<PlayerConfig>();
  const [video, setVideo] = useState<Video>();
  const [videoError, setVideoError] = useState<GetVideoProblemCode | undefined>(undefined);

  const { data, loading, refetch } = useGetVideoQuery({
    variables: { videoManagerId: videoManagerId || '', videoId: videoId || '' },
    skip: !videoId || !videoManagerId,
    fetchPolicy: 'no-cache',
    onCompleted: (data) => {
      if (data.video.__typename === 'GetVideoProblem') {
        setVideoError(data.video.code);
      } else if (data.video.__typename === 'Video') {
        setVideoError(undefined);
      }
    },
  });

  const videoData = data?.video;

  const handlePlayerConfig = (video: Video) => {
    const videoPlayback = video.__typename === 'Video' ? video.playback : undefined;
    const playbackSettings = videoPlayback?.__typename === 'PlaybackSettings' && videoPlayback;

    if (playbackSettings) {
      const transformed = {
        progressive: playbackSettings.sources.progressive.reduce(
          (acc, source) => {
            const { quality, type, url } = source;
            if (!acc[quality]) {
              acc[quality] = [];
            }
            acc[quality].push({ src: url, type: type as SourceType });
            return acc;
          },
          {} as Record<string, { src: string; type: SourceType }[]>
        ),
      };

      const config: PlayerConfig = {
        sources: {
          progressive: transformed.progressive,
        },
      };

      setPlayerConfig(config);
    }
  };

  const handleVideoData = (videoData: Video) => {
    if (videoData.__typename === 'Video' && videoData.playback.__typename === 'PlaybackSettings') {
      setVideo({
        id: videoData.id,
        title: videoData.title,
        duration: videoData.duration,
        createdDate: videoData.createdDate,
        thumbnail: videoData.thumbnail,
        playback: videoData.playback,
        securityPolicy: videoData.securityPolicy,
        channelId: videoData.channelId,
      });
    } else {
      setVideo(undefined);
    }
  };

  useEffect(() => {
    if (videoData && videoData.__typename === 'Video') {
      handlePlayerConfig(videoData);
      handleVideoData(videoData);
    }
  }, [videoData]);

  const onGetVideoData = useCallback(
    async (videoManagerId?: VideoManager['id'], videoId?: Video['id']) => {
      const result = await refetch({ videoManagerId, videoId });
      const video = result.data?.video;

      if (video.__typename === 'Video') {
        handlePlayerConfig(video);
        handleVideoData(video);
      }
    },
    [refetch]
  );

  return {
    loading,
    video,
    playerConfig,
    videoError,
    onGetVideoData,
    onClearVideoError: () => setVideoError(undefined),
  };
}
